import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../Auth/AuthContext";

/**
 * Contatos silenciados pelo usuário logado.
 *
 * Silenciar é uma preferência pessoal (igual ao WhatsApp): a lista vem de
 * GET /contact-mutes e só contém os mutes deste usuário. Em ticket de grupo o
 * contato do ticket é o próprio grupo, então silenciar um grupo não afeta a
 * conversa privada de nenhum integrante — são contactIds diferentes.
 *
 * A expiração é avaliada na LEITURA (isContactMuted), não por timer: um mute de
 * 8 horas simplesmente deixa de valer quando a hora passa, sem re-render nem
 * setTimeout pendurado.
 */

const MutedContactsContext = createContext({
  isContactMuted: () => false,
  isContactMutedRef: { current: () => false },
  muteContact: async () => {},
  unmuteContact: async () => {},
});

export const MutedContactsProvider = ({ children }) => {
  const { user } = useContext(AuthContext);
  // contactId (number) -> mutedUntil (string ISO) | null ("para sempre")
  const [mutes, setMutes] = useState(() => new Map());

  useEffect(() => {
    if (!user?.id) {
      setMutes(new Map());
      return undefined;
    }

    let isMounted = true;
    (async () => {
      try {
        const { data } = await api.get("/contact-mutes");
        if (!isMounted) return;
        const next = new Map();
        (Array.isArray(data) ? data : []).forEach((mute) => {
          next.set(Number(mute.contactId), mute.mutedUntil ?? null);
        });
        setMutes(next);
      } catch (err) {
        // Falhar aqui só significa "nada silenciado": o usuário volta a ouvir
        // todos os alertas, que é o padrão seguro. Não vale um toast de erro.
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [user?.id]);

  const isContactMuted = useCallback(
    (contactId) => {
      if (!contactId) return false;
      const key = Number(contactId);
      if (!mutes.has(key)) return false;

      const mutedUntil = mutes.get(key);
      if (mutedUntil === null) return true; // silenciado para sempre

      const until = Date.parse(mutedUntil);
      return Number.isFinite(until) && until > Date.now();
    },
    [mutes]
  );

  // O handler de socket do NotificationsPopOver vive dentro de um useEffect com
  // dependências fixas; ler `mutes` direto de lá daria stale closure (ou forçaria
  // reconectar o socket a cada mute). O ref sempre aponta para a versão atual.
  const isContactMutedRef = useRef(isContactMuted);
  useEffect(() => {
    isContactMutedRef.current = isContactMuted;
  }, [isContactMuted]);

  const muteContact = useCallback(async (contactId, duration) => {
    if (!contactId) return;
    try {
      const { data } = await api.put(`/contacts/${contactId}/mute`, { duration });
      setMutes((prev) => {
        const next = new Map(prev);
        next.set(Number(contactId), data?.mutedUntil ?? null);
        return next;
      });
    } catch (err) {
      toastError(err);
    }
  }, []);

  const unmuteContact = useCallback(async (contactId) => {
    if (!contactId) return;
    try {
      await api.delete(`/contacts/${contactId}/mute`);
      setMutes((prev) => {
        const next = new Map(prev);
        next.delete(Number(contactId));
        return next;
      });
    } catch (err) {
      toastError(err);
    }
  }, []);

  const value = useMemo(
    () => ({ isContactMuted, isContactMutedRef, muteContact, unmuteContact }),
    [isContactMuted, muteContact, unmuteContact]
  );

  return (
    <MutedContactsContext.Provider value={value}>
      {children}
    </MutedContactsContext.Provider>
  );
};

export const useMutedContacts = () => useContext(MutedContactsContext);

export { MutedContactsContext };
